// Queries powering the CustomerProfile master view at /customers/:id.
// Reuses existing tables — no new schema dependency.

import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { computeHealth, type HealthSignal } from './healthScore';

export interface CustomerProjectRow {
  id: string;
  name: string;
  status: string;
  product_line_id: string | null;
  value_amount: number;
  created_at: string;
  started_at: string | null;
  expected_end_date: string | null;
  completed_at: string | null;
}

export interface CustomerTaskRow {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  due_date: string | null;
  project_id: string | null;
}

export interface CustomerActivityRow {
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  created_at: string;
  user_id: string | null;
}

export interface CustomerQuoteRow {
  id: string;
  quote_number: string;
  status: string;
  value_amount: number;
  value_currency: string;
  sent_at: string | null;
  accepted_at: string | null;
  valid_until: string | null;
  created_at: string;
}

export interface CustomerInvoiceRow {
  id: string;
  invoice_number: string;
  status: string;
  amount: number;
  currency: string;
  project_id: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface CustomerProfileBundle {
  projects: CustomerProjectRow[];
  tasks: CustomerTaskRow[];
  activity: CustomerActivityRow[];
  quotes: CustomerQuoteRow[];
  invoices: CustomerInvoiceRow[];
  health: HealthSignal;
  kpis: {
    activeProjects: number;
    completedProjects: number;
    acceptedQuoteCount: number;
    lifetimeRevenue: number;
    paidInvoiceCount: number;
    openInvoiceAmount: number;
    contactsCount: number;
  };
}

async function fetchCustomerProfile(orgId: string, customerId: string): Promise<CustomerProfileBundle> {
  const [projectsRes, contactsRes, activityRes, quotesRes] = await Promise.all([
    supabase
      .from('projects')
      .select(
        'id, name, status, product_line_id, value_amount, created_at, started_at, expected_end_date, completed_at',
      )
      .eq('organization_id', orgId)
      .eq('company_id', customerId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
    supabase
      .from('contacts')
      .select('id')
      .eq('organization_id', orgId)
      .eq('company_id', customerId)
      .is('deleted_at', null),
    supabase
      .from('audit_log')
      .select('id, action, entity_type, entity_id, created_at, user_id')
      .eq('organization_id', orgId)
      .eq('entity_type', 'companies')
      .eq('entity_id', customerId)
      .order('created_at', { ascending: false })
      .limit(50),
    supabase
      .from('quotes')
      .select('id, quote_number, status, value_amount, value_currency, sent_at, accepted_at, valid_until, created_at')
      .eq('organization_id', orgId)
      .eq('customer_id', customerId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
  ]);

  const projects = (projectsRes.data ?? []) as CustomerProjectRow[];
  const projectIds = projects.map((p) => p.id);
  const quotes = (quotesRes.data ?? []) as CustomerQuoteRow[];

  // Tasks for any of this customer's projects (indirect linkage).
  let tasks: CustomerTaskRow[] = [];
  if (projectIds.length > 0) {
    const tasksRes = await supabase
      .from('tasks')
      .select('id, title, status, priority, due_date, project_id')
      .eq('organization_id', orgId)
      .in('project_id', projectIds)
      .is('deleted_at', null)
      .not('status', 'in', '("done","cancelled")')
      .order('due_date', { ascending: true, nullsFirst: false })
      .limit(50);
    if (!tasksRes.error) tasks = tasksRes.data;
  }

  // Project-scoped audit log (recent activity per project) — merge into the activity feed.
  let projectActivity: CustomerActivityRow[] = [];
  let invoices: CustomerInvoiceRow[] = [];
  if (projectIds.length > 0) {
    const [pActivityRes, invoicesRes] = await Promise.all([
      supabase
        .from('audit_log')
        .select('id, action, entity_type, entity_id, created_at, user_id')
        .eq('organization_id', orgId)
        .eq('entity_type', 'projects')
        .in('entity_id', projectIds)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('project_invoices')
        .select('id, invoice_number, status, amount, currency, project_id, sent_at, paid_at, created_at')
        .eq('organization_id', orgId)
        .in('project_id', projectIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false }),
    ]);
    if (!pActivityRes.error) projectActivity = pActivityRes.data;
    if (!invoicesRes.error) invoices = invoicesRes.data;
  }

  const activity = [...((activityRes.data ?? []) as CustomerActivityRow[]), ...projectActivity]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 50);

  const lastActivityIso = activity[0]?.created_at ?? null;
  const daysSinceLastActivity =
    lastActivityIso === null
      ? null
      : Math.floor((Date.now() - new Date(lastActivityIso).getTime()) / (24 * 60 * 60 * 1000));

  const activeProjects = projects.filter((p) => p.status === 'active').length;
  const completedProjects = projects.filter((p) => p.status === 'completed').length;
  const stuckProjects = projects.filter(
    (p) => p.status === 'active' && daysSinceLastActivity !== null && daysSinceLastActivity > 14,
  ).length;

  const acceptedQuotes = quotes.filter((q) => q.status === 'accepted');
  const lifetimeRevenue = acceptedQuotes.reduce((sum, q) => sum + q.value_amount, 0);
  const paidInvoices = invoices.filter((i) => i.status === 'paid');
  // Face value of unpaid invoices. Partial payments are netted on the page, which
  // also loads project_payments (0025).
  const openInvoiceAmount = invoices
    .filter((i) => i.status === 'sent')
    .reduce((sum, i) => sum + i.amount, 0);

  const ninetyDaysAgo = Date.now() - 90 * 24 * 60 * 60 * 1000;
  const paidInvoices90d = paidInvoices.filter(
    (i) => i.paid_at !== null && new Date(i.paid_at).getTime() >= ninetyDaysAgo,
  ).length;

  const health = computeHealth({
    daysSinceLastActivity,
    activeProjects,
    stuckProjects,
    paidInvoices90d,
    openInvoices: invoices.filter((i) => i.status === 'sent').length,
  });

  return {
    projects,
    tasks,
    activity,
    quotes,
    invoices,
    health,
    kpis: {
      activeProjects,
      completedProjects,
      acceptedQuoteCount: acceptedQuotes.length,
      lifetimeRevenue,
      paidInvoiceCount: paidInvoices.length,
      openInvoiceAmount,
      contactsCount: (contactsRes.data ?? []).length,
    },
  };
}

export function useCustomerProfile(orgId: string | null, customerId: string | null) {
  return useQuery({
    queryKey: ['customer-profile', orgId, customerId] as const,
    queryFn: () => {
      if (!orgId || !customerId) {
        return Promise.resolve<CustomerProfileBundle>({
          projects: [],
          tasks: [],
          activity: [],
          quotes: [],
          invoices: [],
          health: { tone: 'unknown', score: 0, reason: '' },
          kpis: {
            activeProjects: 0,
            completedProjects: 0,
            acceptedQuoteCount: 0,
            lifetimeRevenue: 0,
            paidInvoiceCount: 0,
            openInvoiceAmount: 0,
            contactsCount: 0,
          },
        });
      }
      return fetchCustomerProfile(orgId, customerId);
    },
    enabled: Boolean(orgId && customerId),
    staleTime: 30 * 1000,
  });
}
